import SaleOrderServices from "../services/SalesOrderService";
import invoiceService from "../services/InvoiceService";
import { CartState } from "../types/cart";
import {
  HeldCheckoutOrder,
  cartFingerprint,
  clearHeldCheckoutOrder,
  loadCheckoutFingerprint,
  loadHeldCheckoutOrder,
  saveCheckoutFingerprint,
  saveHeldCheckoutOrder,
} from "./checkoutSession";

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export class CheckoutError extends Error {
  code?: string;
  productCode?: string;
  constructor(message: string, code?: string, productCode?: string) {
    super(message);
    this.code = code;
    this.productCode = productCode;
  }
}

function axiosErrorBody(err: unknown): Record<string, unknown> {
  const axiosErr = err as {
    response?: { status?: number; data?: Record<string, unknown> | string };
  };
  const data = axiosErr.response?.data;
  if (data && typeof data === "object") return data;
  return {};
}

export function parseItemControlError(err: unknown): CheckoutError | null {
  const body = axiosErrorBody(err);
  const code = String(body.code ?? body.error ?? body.message ?? "");
  const combined = `${code} ${JSON.stringify(body)}`;
  if (/NotAuthorizedForItem/i.test(combined)) {
    return new CheckoutError(
      "not_authorized_item",
      "NotAuthorizedForItem",
      String(body.itemCode ?? body.productCode ?? body.code ?? "")
    );
  }
  if (/ConsumptionExceeded/i.test(combined)) {
    return new CheckoutError(
      "consumption_exceeded",
      "ConsumptionExceeded",
      String(body.itemCode ?? body.productCode ?? body.code ?? "")
    );
  }
  return null;
}

let ensureInFlight: Promise<HeldCheckoutOrder> | null = null;

export async function ensureCheckoutOrder(params: {
  cart: CartState;
  paymentMethod: number;
  deliveryDetails?: Record<string, unknown>;
}): Promise<HeldCheckoutOrder> {
  if (ensureInFlight) return ensureInFlight;
  ensureInFlight = ensureCheckoutOrderInner(params).finally(() => {
    ensureInFlight = null;
  });
  return ensureInFlight;
}

async function ensureCheckoutOrderInner(params: {
  cart: CartState;
  paymentMethod: number;
  deliveryDetails?: Record<string, unknown>;
}): Promise<HeldCheckoutOrder> {
  const fp = cartFingerprint(params.cart.items);
  const held = loadHeldCheckoutOrder();
  const prevFp = held?.fingerprint || loadCheckoutFingerprint();

  // Same cart: reuse the unpaid order. Changing payment method must not mint a new number.
  if (held?.orderId && prevFp === fp) {
    const reused: HeldCheckoutOrder = {
      ...held,
      paymentMethod: params.paymentMethod,
      fingerprint: fp,
    };
    saveHeldCheckoutOrder(reused);
    saveCheckoutFingerprint(fp);
    return reused;
  }

  if (held?.orderId) {
    try {
      await SaleOrderServices.deleteSaleOrder(held.orderId);
    } catch {
      /* best-effort — still create a fresh order for the new cart */
    }
    clearHeldCheckoutOrder();
  }

  const payload = {
    date: new Date().toISOString(),
    items: params.cart.items.map((line) => ({
      productId: line.product.id,
      quantity: line.quantity,
    })),
    amount: params.cart.total,
    status: 0,
    paymentMethod: params.paymentMethod,
    deliveryDetails: params.deliveryDetails,
  };

  try {
    const response = await SaleOrderServices.postSaleOrder(payload);
    const data = response.data as Record<string, unknown>;
    const orderId = String(data.id ?? data.Id ?? "");
    if (!orderId) {
      throw new CheckoutError("missing_order_id");
    }

    let orderNumber = String(data.number ?? data.Number ?? orderId);
    for (let i = 0; i < 5; i += 1) {
      try {
        const lookup = await SaleOrderServices.getSaleOrderById(orderId);
        const row = lookup.data as Record<string, unknown>;
        orderNumber = String(row.number ?? row.Number ?? orderNumber);
        break;
      } catch (err) {
        const status = (err as { response?: { status?: number } }).response?.status;
        if (status === 401) {
          throw new CheckoutError("session_expired", "401");
        }
        if (i === 4) throw new CheckoutError("order_not_queryable");
        await wait(400 * (i + 1));
      }
    }

    let invoiceId: string | undefined;
    let invoiceFailed = false;
    try {
      const invoiceRes = await invoiceService.createCheckoutInvoice({
        invoiceNumber: `INV-${Date.now()}`,
        orderNumber,
        dueDate: new Date().toISOString(),
        subtotal: params.cart.total,
        tax: "0",
        total: params.cart.total,
      });
      const inv = invoiceRes.data as Record<string, unknown>;
      invoiceId = String(inv.id ?? inv.Id ?? "") || undefined;
    } catch {
      invoiceFailed = true;
    }

    const next: HeldCheckoutOrder = {
      orderId,
      orderNumber,
      invoiceId,
      paymentMethod: params.paymentMethod,
      fingerprint: fp,
    };
    saveHeldCheckoutOrder(next);
    saveCheckoutFingerprint(fp);
    return { ...next, invoiceFailed };
  } catch (err) {
    const itemErr = parseItemControlError(err);
    if (itemErr) throw itemErr;
    if (err instanceof CheckoutError) throw err;
    throw new CheckoutError(
      String(
        (err as { response?: { data?: { message?: string; error?: string } } })
          .response?.data?.message ??
          (err as { response?: { data?: { error?: string } } }).response?.data?.error ??
          (err as Error).message ??
          "order_create_failed"
      )
    );
  }
}

export async function deleteHeldOrderOnce(): Promise<void> {
  const held = loadHeldCheckoutOrder();
  if (!held?.orderId) return;
  try {
    await SaleOrderServices.deleteSaleOrder(held.orderId);
  } catch {
    /* already gone */
  }
  clearHeldCheckoutOrder();
}
